import type {
  ElementGroupState,
  GemMeterElement,
  GemMeterMetric,
  GemMeterStandard,
  RuleHandler,
  ScreenType,
  StandardState,
} from '../../../types';

import _cloneDeep from 'lodash.clonedeep';

import CHECK_LIST, { SHOPABLE_PREFIX } from '../../../constants/rulesConstants';
import RuleHandlers from '../../rules';
import { hasNotProductInShopifyStore } from '../useGemMeterHelper';
import {
  commitClearDataSetting,
  commitResetStore,
  commitStandardList,
  getConvertableStandard,
  getElementFromStore,
  getElementGroup,
  getElementOfRule,
  getGroupItem,
  getPageElements,
  getPageMetric,
  getScreenType,
  getShopableStandards,
  getStandardByRuleId,
} from '../useGemMeterStore';
import emitter from '@/emitter';
import { removeElementFromError } from '../useGemMeterController';
import GemMeterBaseRule from '../../rules/GemMeterBaseRule';

export default class GemMeterHelper {
  static updateElementGroupWhenAdd(
    elementGroups: ElementGroupState,
    screenType: ScreenType,
    elementKey: string,
    elementMerId: string,
  ) {
    const ruleIds = GemMeterHelper.convertKeyToGroup(elementKey);
    if (ruleIds === null) {
      return elementGroups;
    }

    ruleIds.forEach((ruleId) => {
      const gemElement = GemMeterHelper.createGemMeterElement(elementKey, elementMerId);
      if (!elementGroups[screenType].hasOwnProperty(ruleId)) {
        elementGroups.lg[ruleId] = {};
        elementGroups.lg[ruleId][elementMerId] = gemElement;
        elementGroups.md[ruleId] = {};
        elementGroups.md[ruleId][elementMerId] = gemElement;
        elementGroups.sm[ruleId] = {};
        elementGroups.sm[ruleId][elementMerId] = gemElement;
        elementGroups.xs[ruleId] = {};
        elementGroups.xs[ruleId][elementMerId] = gemElement;
      } else {
        elementGroups.lg[ruleId][elementMerId] = gemElement;
        elementGroups.md[ruleId][elementMerId] = gemElement;
        elementGroups.sm[ruleId][elementMerId] = gemElement;
        elementGroups.xs[ruleId][elementMerId] = gemElement;
      }
    });

    return elementGroups;
  }

  static updateElementGroupWhenRemove(elementGroups: ElementGroupState, ruleId: string, elementMerId: string) {
    if (elementGroups.lg[ruleId] !== undefined) {
      delete elementGroups.lg[ruleId][elementMerId];
    }
    if (elementGroups.md[ruleId] !== undefined) {
      delete elementGroups.md[ruleId][elementMerId];
    }
    if (elementGroups.sm[ruleId] !== undefined) {
      delete elementGroups.sm[ruleId][elementMerId];
    }
    if (elementGroups.xs[ruleId] !== undefined) {
      delete elementGroups.xs[ruleId][elementMerId];
    }

    return elementGroups;
  }

  static convertKeyToGroup(elementKey: string) {
    switch (elementKey) {
      case 'heading':
        return [CHECK_LIST.DIFFERENT_HEADING_FONT_FAMILY.id];
      case 'button':
      case 'p-cart-button':
      case 'p-view-more':
      case 'submit-button':
      case 'dynamic-button':
        return [CHECK_LIST.NO_CALL_TO_ACTION_FOUND.id, CHECK_LIST.DIFFERENT_BUTTON_FONT_FAMILY.id];
      case 'product-list':
        return [CHECK_LIST.NO_CALL_TO_ACTION_FOUND.id];
      case 'loox-reviews-carousel':
      case 'judge-reviews':
      case 'judge-reviews-carousel':
      case 'product-reviews':
      case 'lai-aliexpress-reviews':
      case 'yotpo-reviews':
      case 'yotpo-featured':
      case 'loox':
      case 'ali-reviews':
      case 'growave':
      case 'stamped-product-reviews':
      case 'ryviu-product-reviews':
      case 'opinew-product-reviews':
      case 'rivyo-product-reviews':
        return [CHECK_LIST.WEAK_SENSE_OF_TRUST.id];
      case 'hero-banner':
      case 'parallax':
      case 'image':
      case 'image-before-after':
        return [CHECK_LIST.MISSING_LAZY_LOADING.id];
      default:
        return null;
    }
  }

  static createGemMeterElement(elementKey: string, elementMerId: string) {
    const fonts = getFonts(elementMerId, elementKey);

    return {
      key: elementKey,
      id: elementMerId.substring(3),
      merId: elementMerId,
      type: elementMerId.split('-').shift() === '#m' ? 'module' : 'element',
      isPassed: false,
      status: 'new',
      visible: true,
      fontFamily: fonts?.fontFamily,
      fontWeight: fonts?.fontWeight,
    } as GemMeterElement;
  }

  static createElementInfo() {
    return {
      lg: {
        visible: true,
      },
      md: {
        visible: true,
      },
      sm: {
        visible: true,
      },
      xs: {
        visible: true,
      },
    };
  }

  static isStandaloneRule(ruleId: string) {
    return [CHECK_LIST.DIFFERENT_HEADING_FONT_FAMILY.id, CHECK_LIST.DIFFERENT_BUTTON_FONT_FAMILY.id].includes(ruleId);
  }

  static updateStoreWhenAddElement(
    standards: StandardState,
    metric: GemMeterMetric,
    ruleId: string,
    screenType: ScreenType,
    isValid: boolean | null,
    gemElement: GemMeterElement,
  ) {
    if (!standards.hasOwnProperty(ruleId)) {
      return {
        newStandard: standards,
        newMetric: metric,
      };
    }

    // Only update status for current screen type
    if (isValid === false) {
      metric[screenType].errors += 1;
      standards[ruleId].screen[screenType].errorElements[gemElement.merId] = gemElement;
      standards[ruleId].screen[screenType].elementLength += 1;
    }
    if (isValid === true) {
      metric[screenType].fixed += 1;
      standards[ruleId].screen[screenType].elementLength += 1;
    }

    // Assume error and visible on other screen
    const screenTypeList: ScreenType[] = ['lg', 'md', 'sm', 'xs'];
    screenTypeList.forEach((screenT) => {
      if (screenT !== screenType) {
        metric[screenT].errors += 1;
        standards[ruleId].screen[screenT].elementLength += 1;
        standards[ruleId].screen[screenT].errorElements[gemElement.merId] = _cloneDeep(gemElement);
      }
    });

    return {
      newStandard: standards,
      newMetric: metric,
    };
  }

  static getPageRuleById(key: string) {
    switch (key) {
      case CHECK_LIST.NO_CALL_TO_ACTION_FOUND.id:
        return RuleHandlers.NoCallToActionFoundRule;
      case CHECK_LIST.PAGE_HEIGHT_IS_TOO_LONG.id:
        return RuleHandlers.PageHightIsTooLongRule;
      case CHECK_LIST.WEAK_SENSE_OF_TRUST.id:
        return RuleHandlers.WeakSenseOfTrustRule;
      case CHECK_LIST.WEAK_SEO.id:
        return RuleHandlers.WeakSEO;
      case CHECK_LIST.MISSING_LAZY_LOADING.id:
        return RuleHandlers.MissingLazyLoading;
      default:
        return null;
    }
  }

  static updateStoreWhenRunPageRule(
    standards: StandardState,
    metric: GemMeterMetric,
    ruleId: string,
    screenType: ScreenType,
    isValid: boolean,
  ) {
    if (!standards.hasOwnProperty(ruleId)) {
      return {
        newStandards: standards,
        newMetric: metric,
      };
    }

    const currentStatus = standards[ruleId].screen[screenType].status;
    if (currentStatus === 'skipped') {
      return {
        newStandards: standards,
        newMetric: metric,
      };
    }

    if (!isValid) {
      if (currentStatus === null) {
        metric[screenType].errors += 1;
      }
      if (currentStatus === 'fixed') {
        metric[screenType].fixed -= 1;
        metric[screenType].errors += 1;
      }
      standards[ruleId].screen[screenType].status = 'error';
    } else {
      if (currentStatus === null) {
        metric[screenType].fixed += 1;
      }
      if (currentStatus === 'error') {
        metric[screenType].fixed += 1;
        metric[screenType].errors -= 1;
      }
      standards[ruleId].screen[screenType].status = 'fixed';
    }

    return {
      newStandards: standards,
      newMetric: metric,
    };
  }

  static getRule(elementKey: string): RuleHandler | null {
    switch (elementKey) {
      case 'heading':
        return {
          additionalSelector: '.text-edit :first-child',
          handlers: [RuleHandlers.TextSizeRule, RuleHandlers.TitleTooLongRule, RuleHandlers.DifferentHeadingFontFamily],
        };
      case 'p-title':
        return {
          additionalSelector: '.gf_product-title',
          handlers: [RuleHandlers.TextSizeRule],
        };
      case 'text-field':
      case 'email-field':
        return {
          additionalSelector: '.gf_textfield',
          handlers: [RuleHandlers.InputFieldNameRule],
        };
      case 'text-area':
        return {
          additionalSelector: '.gf_textarea',
          handlers: [RuleHandlers.InputFieldNameRule],
        };
      case 'text-block':
        return {
          additionalSelector: '.text-edit',
          handlers: [RuleHandlers.TextSizeRule],
        };
      case 'p-description':
        return {
          additionalSelector: '.gf_product-desc',
          handlers: [RuleHandlers.TextSizeRule],
        };
      case 'button':
      case 'p-view-more':
        return {
          additionalSelector: '.button',
          handlers: [
            RuleHandlers.ContrastRule,
            RuleHandlers.ButtonMinSizeRule,
            RuleHandlers.MissingButtonLink,
            RuleHandlers.ButtonTextLengthRule,
            RuleHandlers.DifferentButtonFontFamily,
          ],
        };
      case 'submit-button':
        return {
          additionalSelector: '.button',
          handlers: [
            RuleHandlers.ContrastRule,
            RuleHandlers.ButtonMinSizeRule,
            RuleHandlers.ButtonTextLengthRule,
            RuleHandlers.DifferentButtonFontFamily,
          ],
        };
      case 'dynamic-button':
        return {
          additionalSelector: '.shopify-payment-button__button',
          handlers: [
            RuleHandlers.ContrastRule,
            RuleHandlers.ButtonMinSizeRule,
            RuleHandlers.ButtonTextLengthRule,
            RuleHandlers.DifferentButtonFontFamily,
          ],
        }
      case 'p-cart-button': {
        if (hasNotProductInShopifyStore()) {
          return null;
        }

        return {
          additionalSelector: '.button',
          handlers: [
            RuleHandlers.ContrastRule,
            RuleHandlers.ButtonMinSizeRule,
            RuleHandlers.ButtonTextLengthRule,
            RuleHandlers.DifferentButtonFontFamily,
          ],
        };
      }
      case 'vimeo':
      case 'youtube':
        return {
          additionalSelector: '.module',
          handlers: [RuleHandlers.MissingMediaLinkRule],
        };
      case 'video-bg':
        return {
          additionalSelector: '.video-background',
          handlers: [RuleHandlers.MissingVideoBgLinkRule],
        };
      case 'video-popup': {
        return {
          additionalSelector: '.video-popup',
          handlers: [RuleHandlers.MissingPopupLinkRule],
        };
      }
      case 'count-down':
        return {
          additionalSelector: '.count-down-wrap',
          handlers: [RuleHandlers.CountdownTimerHourRule],
        };
      default:
        return null;
    }
  }

  static checkElementByRule(element: any, ruleId: string, isStandaloneRule?: boolean) {
    let isValid = null;
    const screenType = getScreenType();
    const elementGroups = getElementGroup();
    const elements = elementGroups[screenType][ruleId] || {};

    const rules = GemMeterHelper.getRule(element.key);
    if (!rules) {
      return isValid;
    }

    const selector = `${element.merId} ${rules.additionalSelector}`;

    for (const handler of rules.handlers) {
      if (handler.ruleId === ruleId) {
        if (isStandaloneRule) {
          handler.validate(`EDIT@${element.merId}`, screenType);
          if (elements[element.merId]) {
            const fonts = getFonts(element.merId, element.key);
            elements[element.merId].fontFamily = fonts?.fontFamily;
            elements[element.merId].fontWeight = fonts?.fontWeight;
          }

          return null;
        } else {
          isValid = handler.validate(selector, screenType);
        }
        break;
      }
    }

    return isValid;
  }

  static updateStoreWhenEditElement(
    metric: GemMeterMetric,
    standards: StandardState,
    ruleId: string,
    screenType: ScreenType,
    elementMerId: string,
    isValid: boolean,
  ) {
    const currentStatus = standards[ruleId].screen[screenType].errorElements[elementMerId].status;
    // Skip if element is skip before
    if (currentStatus === 'skip') {
      return { newStandards: standards, newMetric: metric };
    }

    standards[ruleId].screen[screenType].errorElements[elementMerId].status = isValid ? 'fixed' : 'new';
    if (isValid && currentStatus !== 'fixed') {
      metric[screenType].fixed += 1;
      metric[screenType].errors -= 1;
      // If user in view all mode or not view this group detail
      const groupItem = getGroupItem();
      if (!groupItem || groupItem.id !== ruleId) {
        // Remove element from error
        removeElementFromError(elementMerId.substring(3), ruleId);
      }
    }

    if (!isValid && currentStatus === 'fixed') {
      metric[screenType].fixed -= 1;
      metric[screenType].errors += 1;
      standards[ruleId].screen[screenType].errorElements[elementMerId].visible = true;
    }

    return { newStandards: standards, newMetric: metric };
  }

  static updateStoreWhenRemoveElement(
    standards: StandardState,
    metric: GemMeterMetric,
    ruleId: string,
    elementMerId: string,
  ) {
    const elementInRule = getElementOfRule(elementMerId);
    if (!elementInRule) {
      return {
        newStandards: standards,
        newMetric: metric,
      };
    }

    for (const screenType of ['lg', 'md', 'sm', 'xs'] as ScreenType[]) {
      // Ignore if element not visible on screen
      if (!elementInRule[screenType].visible) {
        continue;
      }

      if (!standards.hasOwnProperty(ruleId)) {
        continue;
      }

      standards[ruleId].screen[screenType].elementLength -= 1;
      const gemElement = standards[ruleId].screen[screenType].errorElements[elementMerId];
      // Element does exits in error list so must be fixed
      if (!gemElement) {
        metric[screenType].fixed -= 1;
        continue;
      }

      delete standards[ruleId].screen[screenType].errorElements[elementMerId];
      if (gemElement.status === 'new') {
        metric[screenType].errors -= 1;
      } else {
        metric[screenType].skipped -= 1;
      }
    }

    return {
      newStandards: standards,
      newMetric: metric,
    };
  }

  static updateElementVisibility(elementId: string, screenType: ScreenType, isVisible: boolean, prefixId: string) {
    const elementMerId = `${prefixId}-${elementId}`;
    // Ignore if element info not found or new visible same as current visible status
    const elementInfo = getElementOfRule(elementMerId);
    if (!elementInfo || elementInfo[screenType].visible === isVisible) {
      return;
    }

    // Ignore if element not found in Vuex store
    const rawElement = getElementFromStore(elementId);
    const element = JSON.parse(rawElement);
    if (!element) {
      return false;
    }

    // Update element group base on visibility
    const groupRuleIds = GemMeterHelper.convertKeyToGroup(element.key) || [];
    const elementGroup = getElementGroup();
    if (isVisible) {
      // If visible then add element to group
      groupRuleIds.forEach((ruleId) => {
        const gemElement = GemMeterHelper.createGemMeterElement(element.key, element.merId);
        elementGroup[screenType][ruleId][element.merId] = gemElement;
        // const productMerIds = GemMeterHelper.getProductAndProductListIdsInGroup(ruleId);
        // productMerIds.forEach(product => {
        //   const gemElement = GemMeterHelper.createGemMeterElement(product.key, product.merId);
        //   elementGroup[screenType][ruleId][product.merId] = gemElement
        // })
      });
    } else {
      // If hidden then remove element from group
      groupRuleIds.forEach((ruleId) => {
        delete elementGroup[screenType][ruleId][element.merId];
        // const productMerIds = GemMeterHelper.getProductAndProductListIdsInGroup(ruleId);
        // productMerIds.forEach(product => {
        //   delete elementGroup[screenType][ruleId][product.merId]
        // })
      });
    }

    const relatedRules = GemMeterHelper.getRule(element.key);
    const currentScreenType = getScreenType();
    if (relatedRules) {
      let shopableStandards = getShopableStandards();
      let convertableStandards = getConvertableStandard();
      let metric = getPageMetric();
      const elementOfRule = getElementOfRule(elementMerId);
      // Show element
      if (isVisible) {
        elementOfRule[screenType].visible = true;
        // Change visible on same screen
        if (currentScreenType === screenType) {
          const selector = `${elementMerId} ${relatedRules.additionalSelector}`;
          relatedRules.handlers.forEach((handler) => {
            const ruleId = handler.ruleId;
            if (GemMeterHelper.isStandaloneRule(ruleId)) {
              handler.validate(`SHOW@${elementMerId}`, screenType);

              return;
            }

            const isValid = handler.validate(selector, screenType);
            const gemElement = GemMeterHelper.createGemMeterElement(element.key, elementMerId);
            if (ruleId.includes(SHOPABLE_PREFIX)) {
              const { newStandards, newMetric } = GemMeterHelper.updateStoreWhenShowElement(
                shopableStandards,
                metric,
                ruleId,
                screenType,
                gemElement,
                isValid,
              );
              shopableStandards = newStandards;
              metric = newMetric;
            } else {
              const { newStandards, newMetric } = GemMeterHelper.updateStoreWhenShowElement(
                convertableStandards,
                metric,
                ruleId,
                screenType,
                gemElement,
                isValid,
              );
              convertableStandards = newStandards;
              metric = newMetric;
            }
          });
        } else {
          // Change visible on other screen
          relatedRules.handlers.forEach((handler) => {
            const ruleId = handler.ruleId;
            if (GemMeterHelper.isStandaloneRule(ruleId)) {
              handler.validate(elementMerId, screenType);

              return;
            }

            // Assume element is always error
            metric[screenType].errors += 1;
            const gemElement = GemMeterHelper.createGemMeterElement(element.key, elementMerId);
            if (ruleId.includes(SHOPABLE_PREFIX)) {
              shopableStandards[ruleId].screen[screenType].errorElements[elementMerId] = _cloneDeep(gemElement);
              shopableStandards[ruleId].screen[screenType].elementLength += 1;
            } else {
              convertableStandards[ruleId].screen[screenType].errorElements[elementMerId] = _cloneDeep(gemElement);
              convertableStandards[ruleId].screen[screenType].elementLength += 1;
            }
          });
        }
      } else {
        // Hide element
        relatedRules.handlers.forEach((handler) => {
          const ruleId = handler.ruleId;
          if (GemMeterHelper.isStandaloneRule(ruleId)) {
            handler.validate(`HIDE@${elementMerId}`, screenType);

            return;
          }

          if (ruleId.includes(SHOPABLE_PREFIX)) {
            const { newStandards, newMetric } = GemMeterHelper.updateStoreWhenHideElement(
              shopableStandards,
              metric,
              ruleId,
              screenType,
              elementMerId,
            );
            shopableStandards = newStandards;
            metric = newMetric;
          } else {
            const { newStandards, newMetric } = GemMeterHelper.updateStoreWhenHideElement(
              convertableStandards,
              metric,
              ruleId,
              screenType,
              elementMerId,
            );
            convertableStandards = newStandards;
            metric = newMetric;
          }
        });
        elementOfRule[screenType].visible = false;
      }

      emitter.emit('gem-meter:remove-element', elementId);
    }
  }

  static updateStoreWhenHideElement(
    standards: StandardState,
    metric: GemMeterMetric,
    ruleId: string,
    screenType: ScreenType,
    elementMerId: string,
  ) {
    const errorElement = standards[ruleId].screen[screenType].errorElements[elementMerId];
    if (!errorElement) {
      metric[screenType].fixed -= 1;
    } else {
      switch (errorElement.status) {
        case 'fixed': {
          metric[screenType].fixed -= 1;
          break;
        }
        case 'skip': {
          metric[screenType].skipped -= 1;
          break;
        }
        case 'new': {
          metric[screenType].errors -= 1;
          break;
        }
      }
      delete standards[ruleId].screen[screenType].errorElements[elementMerId];
    }

    standards[ruleId].screen[screenType].elementLength -= 1;

    return {
      newStandards: standards,
      newMetric: metric,
    };
  }

  static updateStoreWhenShowElement(
    standards: StandardState,
    metric: GemMeterMetric,
    ruleId: string,
    screenType: ScreenType,
    gemElement: GemMeterElement,
    isValid: boolean,
  ) {
    standards[ruleId].screen[screenType].elementLength += 1;
    if (!isValid) {
      standards[ruleId].screen[screenType].errorElements[gemElement.merId] = gemElement;
      metric[screenType].errors += 1;
    } else {
      metric[screenType].fixed += 1;
    }

    return {
      newStandards: standards,
      newMetric: metric,
    };
  }

  static getProductAndProductListIdsInGroup(ruleId: string) {
    const screenType = getScreenType();
    const elementGroups = getElementGroup();
    const elements = elementGroups[screenType][ruleId] || {};
    const keys = Object.keys(elements);

    return keys
      .map((key) => elements[key])
      .filter((item) => item.key === 'product' || item.key === 'product-list')
      .map((item) => ({
        merId: item.merId,
        key: item.key,
      }));
  }

  static updateStoreWhenChangeScreen(
    standards: StandardState,
    metric: GemMeterMetric,
    ruleId: string,
    screenType: ScreenType,
    gemElement: GemMeterElement,
    isValid: boolean | null,
  ) {
    // Element not visible
    if (isValid === null) {
      if (standards[ruleId].screen[screenType].errorElements.hasOwnProperty(gemElement.merId)) {
        delete standards[ruleId].screen[screenType].errorElements[gemElement.merId];
        standards[ruleId].screen[screenType].elementLength -= 1;
        metric[screenType].errors -= 1;
      }
    }

    // Change status from error to fixed
    if (
      isValid === true &&
      standards[ruleId].screen[screenType].errorElements.hasOwnProperty(gemElement.merId) &&
      standards[ruleId].screen[screenType].errorElements[gemElement.merId].status === 'new'
    ) {
      metric[screenType].fixed += 1;
      metric[screenType].errors -= 1;
      delete standards[ruleId].screen[screenType].errorElements[gemElement.merId];
    }

    // Change status from fixed to error
    if (isValid === false && !standards[ruleId].screen[screenType].errorElements.hasOwnProperty(gemElement.merId)) {
      metric[screenType].fixed -= 1;
      metric[screenType].errors += 1;
      standards[ruleId].screen[screenType].errorElements[gemElement.merId] = gemElement;
    }
    return {
      newStandards: standards,
      newMetric: metric,
    };
  }

  static getElementIdRecheck(rootElementId: string, ruleId: string) {
    const screenType = getScreenType();
    const standard = getStandardByRuleId(ruleId);
    const elementErrors = standard[ruleId].screen[screenType].errorElements;
    const regex = /-child(.*?)-/;

    if (rootElementId.match(regex)) {
      const splitString = rootElementId.split('-child');
      const splitStringForIndex = rootElementId.split('-');
      return Object.keys(elementErrors)
        .filter((item) => {
          const splitStringForIndexItem = item.split('-');
          return (
            item.includes(`${splitString[0]}-child`) &&
            splitStringForIndex[splitStringForIndex.length - 1] ===
              splitStringForIndexItem[splitStringForIndexItem.length - 1] &&
            splitStringForIndexItem.length - 1 === splitStringForIndex.length
          );
        })
        .map((item) => {
          return item;
        });
    }

    return [rootElementId];
  }

  static convertRuleByAttrName(attributeName: string) {
    switch (attributeName) {
      case 'editLink':
      case 'linkTarget':
        return [CHECK_LIST.MISSING_LINK.id];
      case 'url':
      case 'vimeo-url':
      case 'videobg_youtube':
      case 'videobg_vimeo':
      case 'videobg_custom':
      case 'youtube-url':
      case 'video_source':
      case 'custom-url':
        return [CHECK_LIST.NO_SOURCE_VIDEO.id];
      case 'editText':
      case 'btnText':
      case 'soldOutText':
        return [CHECK_LIST.WEAK_CALL_TO_ACTION.id, CHECK_LIST.HARD_TO_CLICK_BUTTON.id];
      case 'height':
        return [CHECK_LIST.HARD_TO_CLICK_BUTTON.id];
      case 'button-color':
      case 'color':
      case 'text-color':
      case 'fontWeight':
      case 'background-color':
        return [CHECK_LIST.HARD_TO_READ_BUTTON.id];
      case 'font-size':
        return [
          CHECK_LIST.HARD_TO_READ_CONTENT.id,
          CHECK_LIST.HARD_TO_CLICK_BUTTON.id,
          CHECK_LIST.HARD_TO_READ_BUTTON.id,
        ];
      case 'name':
        return [CHECK_LIST.CAN_NOT_SEND_EMAIL.id];
      case 'datetime':
      case 'everGreen':
      case 'dateEvergreen':
      case 'dateDaily':
        return [CHECK_LIST.WEAK_SENSE_OF_URGENCY.id];
      case 'padding-top':
      case 'padding-bottom':
        return [CHECK_LIST.HARD_TO_CLICK_BUTTON.id];
      case 'font':
        return [CHECK_LIST.DIFFERENT_HEADING_FONT_FAMILY.id, CHECK_LIST.DIFFERENT_BUTTON_FONT_FAMILY.id];
      case 'title-length':
        return [CHECK_LIST.TITLE_IS_TOO_LONG.id];
      case 'titleTag':
        return [CHECK_LIST.HARD_TO_READ_CONTENT.id, CHECK_LIST.DIFFERENT_HEADING_FONT_FAMILY.id];
    }

    return [];
  }

  static getGroupLengthByRuleId(ruleId: string, screenType: ScreenType) {
    const elementGroups = getElementGroup();
    if (elementGroups && elementGroups[screenType][ruleId]) {
      return Object.keys(elementGroups[screenType][ruleId]).length;
    }

    return -1;
  }

  static clearStore() {
    const pageElements = getPageElements();
    if (Object.keys(pageElements).length != 0) {
      return;
    }

    const shopableStandards = getShopableStandards();
    const convertableStandards = getConvertableStandard();
    const pageRules = [
      CHECK_LIST.NO_CALL_TO_ACTION_FOUND,
      CHECK_LIST.PAGE_HEIGHT_IS_TOO_LONG,
      CHECK_LIST.WEAK_SENSE_OF_TRUST,
    ];

    const keepData: Record<string, GemMeterStandard> = {};
    pageRules.forEach((rule) => {
      if (rule.id.includes(SHOPABLE_PREFIX)) {
        keepData[rule.id] = _cloneDeep(shopableStandards[rule.id]);
      } else {
        keepData[rule.id] = _cloneDeep(convertableStandards[rule.id]);
      }
    });

    commitResetStore();
    const metric = getPageMetric();
    const newShopableStandards = getShopableStandards();
    const newConvertableStandards = getConvertableStandard();
    Object.keys(keepData).forEach((ruleId) => {
      const standard = keepData[ruleId];
      (['lg', 'md', 'sm', 'xs'] as ScreenType[]).forEach((screenType) => {
        if (standard.screen[screenType] && standard.screen[screenType].status === 'skipped') {
          metric[screenType].skipped += 1;
          if (ruleId.includes(SHOPABLE_PREFIX)) {
            newShopableStandards[ruleId].screen[screenType] = keepData[ruleId].screen[screenType];
          } else {
            newConvertableStandards[ruleId].screen[screenType] = keepData[ruleId].screen[screenType];
          }
        }
      });
    });

    commitClearDataSetting();
    commitStandardList('shopableStandards', newShopableStandards);
    commitStandardList('convertableStandards', newConvertableStandards);
  }

  static isSpecialElemWhenCheckVisibility(domElement: HTMLElement) {
    const { key } = domElement.dataset;
    if (!key) {
      return false;
    }

    return ['vimeo'].includes(key);
  }
}

export const getFonts = (elementMerId: string, type: string) => {
  let prefix = '';
  switch (type) {
    case 'button':
      prefix = 'button';
      break;
    case 'dynamic-button':
      prefix = 'shopify-payment-button__button';
      break;
    default:
      prefix = 'text-edit :first-child';
      break;
  }
  const domElement = GemMeterBaseRule.getElement(`${elementMerId} .${prefix}`);

  if (domElement) {
    const fontFamily = getComputedStyle(domElement).fontFamily;
    const fontWeight = getComputedStyle(domElement).fontWeight;
    return { fontFamily, fontWeight };
  }
  return null;
};
